import React from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';
import { Colors, type ColorsType } from '@common/tokens';
import { Text } from '@common/components';

export type StatusPillVariantType =
  | 'inProgress'
  | 'warning'
  | 'danger'
  | 'success'
  | 'neutral';

const STATUS_PILL_BACKGROUND: Record<StatusPillVariantType, ColorsType> = {
  inProgress: 'skyLightest',
  warning: 'sunLighter',
  danger: 'coralHyperlight',
  success: 'grassLightest',
  neutral: 'steelLighter',
};

type StatusPillProps = {
  children: ReactNode;
  variant?: StatusPillVariantType;
  className?: string;
};

/**
 * Pill that surfaces an item’s status using semantic background colors from `@common/tokens`.
 *
 * **Variants (`StatusPillVariantType`):**
 * - `inProgress` — In a process; action needed.
 * - `warning` — Third party or waiting.
 * - `danger` — Negative / “sad” end; no go.
 * - `success` — Positive / “happy” end; no action needed.
 * - `neutral` — Completed or archived.
 *
 * **Label composition:** follow **“Status [State] [Newer State]”**, at most **28 characters**.
 * Length is not enforced at runtime; follow the contract when composing labels (including when
 * `children` is not a plain string).
 */
export function StatusPill({
  children,
  variant = 'inProgress',
  className,
}: StatusPillProps) {
  const bgToken = STATUS_PILL_BACKGROUND[variant];

  return (
    <span className={clsx('wk-status-pill', className)}>
      <Text size="sm" color="textDefault">
        {children}
      </Text>
      <style jsx>{`
        .wk-status-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 4px 8px;
          border-radius: 8px;
          background-color: ${Colors[bgToken]};
        }
      `}</style>
    </span>
  );
}
